// Composição financeira da order: cascata Previsto (MP, azul) × Realizado
// (extrato, teal), barra de composição "pra onde foi o dinheiro", bloco
// pós-venda e o Resultado real. Os números vêm prontos do motor.
import { FormatIsoDate } from '../../utils/date';
import { FormatMoney } from '../../utils/format';

const money = (v) => FormatMoney(v);
const dateShort = (v) => FormatIsoDate(v, { blank: '' });

function Row({ label, previsto, realizado, delta, negative, strong, hint }) {
    const cls = negative ? 'text-red-600 dark:text-red-400' : '';
    return (
        <tr className={strong ? 'border-t border-mono-300 font-semibold dark:border-mono-600' : ''}>
            <td className="py-0.5 pr-2">
                {label}
                {hint ? <span className="ml-1 text-xs font-normal text-mono-400">{hint}</span> : null}
            </td>
            <td className={`py-0.5 pr-2 text-right font-mono tabular-nums text-[#3483fa] ${cls}`}>
                {previsto == null ? '' : `${negative ? '− ' : ''}${money(Math.abs(previsto))}`}
            </td>
            <td className={`py-0.5 pr-2 text-right font-mono tabular-nums text-teal-600 dark:text-teal-400 ${cls}`}>
                {realizado == null ? '' : `${negative ? '− ' : ''}${money(Math.abs(realizado))}`}
            </td>
            <td className="py-0.5 text-right font-mono text-xs tabular-nums">
                {delta != null && Math.abs(delta) > 0.01 ? (
                    <span className={delta < 0 ? 'text-red-500' : 'text-emerald-500'}>Δ {money(delta)}</span>
                ) : null}
            </td>
        </tr>
    );
}

// Barra única de composição: % da venda que virou líquido vs cada custo.
function WaterfallBar({ previsto }) {
    const total = previsto.venda || 0;
    if (!total) {
        return null;
    }
    const parts = [
        { label: 'Líquido', value: previsto.liquido, cls: 'bg-emerald-500' },
        { label: 'Tarifas', value: previsto.tarifas, cls: 'bg-red-400' },
        { label: 'Frete', value: previsto.frete, cls: 'bg-orange-400' },
        { label: 'Cupom (você)', value: previsto.cupomSeller, cls: 'bg-rose-400' },
        { label: 'Outros', value: previsto.outros, cls: 'bg-mono-400' },
    ].filter((p) => p.value > 0);
    return (
        <div className="mt-2">
            <div className="flex h-4 w-full overflow-hidden rounded" title="composição da venda (valores previstos no ato)">
                {parts.map((p) => (
                    <div key={p.label} className={p.cls} style={{ width: `${Math.max(1, (p.value / total) * 100)}%` }} title={`${p.label}: ${money(p.value)} (${((p.value / total) * 100).toFixed(1)}%)`} />
                ))}
            </div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-mono-500">
                {parts.map((p) => (
                    <span key={p.label} className="inline-flex items-center gap-1">
                        <span className={`inline-block h-2 w-2 rounded-sm ${p.cls}`} />
                        {p.label} {((p.value / total) * 100).toFixed(1)}%
                    </span>
                ))}
            </div>
        </div>
    );
}

export default function MoneyComposition({ money: m, completeness }) {
    if (!m?.previsto && !m?.posVenda) {
        return <p className="text-sm text-mono-400">Sem dados de pagamento pra compor a cascata.</p>;
    }
    const p = m.previsto;
    const realizadoLiquido = m.realizado?.liquido ?? null;
    const aguardandoExtrato = realizadoLiquido == null;
    return (
        <div className="space-y-4">
            {p ? (
                <div>
                    <div className="mb-1 flex items-baseline gap-2 text-xs text-mono-500">
                        <span>
                            Cascata do dinheiro — <span className="font-semibold text-[#3483fa]">Previsto</span> (payment MP) ×{' '}
                            <span className="font-semibold text-teal-600 dark:text-teal-400">Realizado</span> (extrato)
                        </span>
                        {aguardandoExtrato ? (
                            <span className="rounded bg-mono-100 px-1.5 py-0.5 text-mono-500 dark:bg-mono-800" title={completeness?.extrato === 'NOT_COVERED' ? 'o extrato importado ainda não cobre a data de liberação desta order' : ''}>
                                aguardando extrato
                            </span>
                        ) : null}
                    </div>
                    <table className="w-full max-w-xl text-sm">
                        <thead>
                            <tr className="text-left text-xs text-mono-500">
                                <th className="py-1 pr-2 font-normal">rubrica</th>
                                <th className="pr-2 text-right font-normal">Previsto</th>
                                <th className="pr-2 text-right font-normal">Realizado</th>
                                <th className="text-right font-normal">Δ</th>
                            </tr>
                        </thead>
                        <tbody>
                            <Row label="Venda do produto" previsto={p.venda} />
                            <Row label="Tarifas ML + MP" previsto={p.tarifas} negative />
                            {p.frete > 0 ? <Row label="Frete" previsto={p.frete} negative /> : null}
                            {p.cupomSeller > 0 ? <Row label="Cupom (bancado por você)" previsto={p.cupomSeller} negative /> : null}
                            {p.outros > 0 ? <Row label="Outros custos" previsto={p.outros} negative /> : null}
                            <Row
                                label="= Líquido da venda"
                                previsto={p.liquido}
                                realizado={realizadoLiquido}
                                delta={realizadoLiquido != null ? Math.round((realizadoLiquido - p.liquido) * 100) / 100 : null}
                                strong
                            />
                        </tbody>
                    </table>
                    {p.cupomMl > 0 ? (
                        <p className="mt-1 text-xs text-mono-400">
                            + Cupom de {money(p.cupomMl)} bancado pelo Mercado Livre — o comprador pagou menos, mas <span className="font-medium">não afeta seu líquido</span>.
                        </p>
                    ) : null}
                    <WaterfallBar previsto={p} />
                </div>
            ) : null}

            {m.posVenda ? (
                <div>
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-mono-500">Depois da venda (extrato)</p>
                    {m.posVenda.linhas.length === 0 ? (
                        <p className="text-xs text-mono-400">Nenhum movimento pós-venda além da liberação.</p>
                    ) : (
                        <div className="max-w-xl space-y-0.5 text-sm">
                            {m.posVenda.linhas.map((l, i) => (
                                <div key={`${l.description}-${i}`} className="flex items-baseline justify-between gap-2">
                                    <span className="text-mono-600 dark:text-mono-300">
                                        {l.label}
                                        <span className="ml-1 text-xs text-mono-400">{dateShort(l.date)}</span>
                                    </span>
                                    <span className={`font-mono tabular-nums ${l.value < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                        {l.value < 0 ? '− ' : '+ '}
                                        {money(Math.abs(l.value))}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="mt-2 flex max-w-xl items-baseline justify-between border-t border-mono-300 pt-1 dark:border-mono-600">
                        <span className="font-bold">
                            Resultado real do pedido
                            <span className="ml-1 text-xs font-normal text-mono-400" title="ƒ Σ créditos − débitos do extrato, excluindo reservas internas (reserve_*/payout)">
                                ƒ ({m.posVenda.internos} mov. internos ignorados)
                            </span>
                        </span>
                        <span className={`text-lg font-bold tabular-nums ${m.posVenda.resultado >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                            {m.posVenda.resultado >= 0 ? '+ ' : '− '}
                            {money(Math.abs(m.posVenda.resultado))}
                        </span>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
